import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Box, IconButton, Tooltip } from "@mui/material";
import { Dayjs } from "dayjs";

export interface DateRangeValue {
  from: Dayjs | null;
  to: Dayjs | null;
}

interface DateRangeSelectorProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  format?: string;
  disabled?: boolean;
  minDate?: Dayjs | null;
  maxDate?: Dayjs | null;
  compact?: boolean;
}

const getFieldSx = (compact: boolean) => ({
  width: compact ? 180 : undefined,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    background: "transparent",
    fontSize: compact ? "0.7rem" : "0.75rem",
    height: compact ? 30 : 34,
    "& fieldset": { borderColor: "rgba(198, 197, 212, 0.45)" },
    "&:hover fieldset": { borderColor: "#b71c1c" },
    "&.Mui-focused fieldset": { borderColor: "#b71c1c" },
  },
  "& .MuiInputBase-input": {
    padding: compact ? "4px 6px" : "6px 8px",
    fontSize: compact ? "0.7rem" : "0.75rem",
    fontWeight: 600,
    color: "#191c1d",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "rgba(118, 118, 131, 0.7)",
    fontSize: compact ? "0.7rem" : "0.75rem",
    opacity: 1,
  },
  "& .MuiIconButton-root": { padding: 4 },
  "& .MuiSvgIcon-root": { fontSize: compact ? 14 : 16 },
});

export default function DateRangeSelector({
  value,
  onChange,
  format = "DD/MM/YYYY",
  disabled = false,
  minDate = null,
  maxDate = null,
  compact = false,
}: DateRangeSelectorProps) {
  const hasValue = Boolean(value?.from || value?.to);
  const fieldSx = getFieldSx(compact);

  const handleChange = (key: keyof DateRangeValue) => (date: Dayjs | null) => {
    onChange({ ...value, [key]: date });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: compact ? 0.5 : 0.75,
          background: "#ffffff",
          border: "1px solid rgba(198, 197, 212, 0.25)",
          borderRadius: "10px",
          padding: compact ? "2px 6px" : "3px 8px",
          transition: "all 0.2s ease",
          "&:focus-within": {
            borderColor: "#b71c1c",
            boxShadow: "0 0 0 3px rgba(183, 28, 28, 0.1)",
          },
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span
          className="material-symbols-outlined"
          style={{ fontSize: compact ? 16 : 18, color: "#767683" }}
        >
          calendar_month
        </span>

        <Box sx={fieldSx}>
          <DatePicker
            value={value?.from || null}
            onChange={handleChange("from")}
            format={format}
            disabled={disabled}
            minDate={minDate || undefined}
            maxDate={maxDate || undefined}
            slotProps={{
              textField: {
                size: "small",
                placeholder: "From",
                fullWidth: true,
              },
              field: { clearable: false },
            }}
          />
        </Box>

        <span
          className="material-symbols-outlined"
          style={{ fontSize: compact ? 14 : 16, color: "#767683" }}
        >
          arrow_forward
        </span>

        <Box sx={fieldSx}>
          <DatePicker
            value={value?.to || null}
            onChange={handleChange("to")}
            format={format}
            disabled={disabled}
            minDate={minDate || undefined}
            maxDate={maxDate || undefined}
            slotProps={{
              textField: {
                size: "small",
                placeholder: "To",
                fullWidth: true,
              },
              field: { clearable: false },
            }}
          />
        </Box>

        {hasValue && (
          <Tooltip title="Clear date filter" arrow>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => onChange({ from: null, to: null })}
              sx={{ color: "#767683", p: 0.25 }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 16 }}
              >
                close
              </span>
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </LocalizationProvider>
  );
}
